import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Plus, Pencil, Trash2, Briefcase, CheckCircle, XCircle, Bold, Italic, Underline, List, ListOrdered, Link2, AlignLeft, AlignCenter, AlignRight, Mail, Share2, MessageCircle, Copy, Check } from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import UnderlineExt from '@tiptap/extension-underline';
import DOMPurify from 'dompurify';

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL eingeben:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
        title="Fett"
      >
        <Bold className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
        title="Kursiv"
      >
        <Italic className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-300' : ''}`}
        title="Unterstrichen"
      >
        <Underline className="w-4 h-4" />
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
        title="Aufzählung"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
        title="Nummerierte Liste"
      >
        <ListOrdered className="w-4 h-4" />
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={setLink}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-300' : ''}`}
        title="Link einfügen"
      >
        <Link2 className="w-4 h-4" />
      </button>
      <div className="w-px bg-gray-300 mx-1" />
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-300' : ''}`}
        title="Linksbündig"
      >
        <AlignLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-300' : ''}`}
        title="Zentriert"
      >
        <AlignCenter className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'right' }) ? 'bg-gray-300' : ''}`}
        title="Rechtsbündig"
      >
        <AlignRight className="w-4 h-4" />
      </button>
    </div>
  );
};

const JobManagement = () => {
  const { token } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [copiedId, setCopiedId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    location: 'Dachau',
    employment_type: 'Vollzeit',
    requirements: [],
    benefits: [],
    contact_email: 'info@zahnarzt-muresan.de',
    closing_text: 'Wir freuen uns auf Ihre Bewerbung!\n\nIhr Praxisteam der Zahnarztpraxis Muresan-Richter',
    is_active: true,
    order: 0
  });
  const [newRequirement, setNewRequirement] = useState('');
  const [newBenefit, setNewBenefit] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      UnderlineExt,
    ],
    content: formData.description,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, description: editor.getHTML() }));
    },
  });

  const fetchJobs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch jobs:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (editor && editingJob) {
      editor.commands.setContent(formData.description || '');
    }
  }, [editor, editingJob, formData.description]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    // Validate required fields
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Bitte geben Sie einen Stellentitel ein.' });
      setSaving(false);
      return;
    }
    
    if (!formData.description || formData.description === '<p></p>') {
      setMessage({ type: 'error', text: 'Bitte geben Sie eine Beschreibung ein.' });
      setSaving(false);
      return;
    }
    
    const url = editingJob 
      ? `${API_URL}/api/admin/jobs/${editingJob.id}`
      : `${API_URL}/api/admin/jobs`;
    
    const method = editingJob ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: editingJob ? 'Stellenanzeige gespeichert!' : 'Stellenanzeige erstellt!' });
        await fetchJobs();
        setTimeout(() => {
          resetForm();
        }, 1500);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Fehler beim Speichern.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Netzwerkfehler. Bitte versuchen Sie es erneut.' });
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to save job:', error);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Stellenanzeige wirklich löschen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/jobs/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchJobs();
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to delete job:', error);
      }
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      subtitle: job.subtitle || '',
      description: job.description || '',
      location: job.location || 'Dachau',
      employment_type: job.employment_type || 'Vollzeit',
      requirements: job.requirements || [],
      benefits: job.benefits || [],
      contact_email: job.contact_email || 'info@zahnarzt-muresan.de',
      closing_text: job.closing_text || 'Wir freuen uns auf Ihre Bewerbung!\n\nIhr Praxisteam der Zahnarztpraxis Muresan-Richter',
      is_active: job.is_active !== false,
      order: job.order || 0
    });
    if (editor) {
      editor.commands.setContent(job.description || '');
    }
    setShowModal(true);
    setMessage({ type: '', text: '' });
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingJob(null);
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      location: 'Dachau',
      employment_type: 'Vollzeit',
      requirements: [],
      benefits: [],
      contact_email: 'info@zahnarzt-muresan.de',
      closing_text: 'Wir freuen uns auf Ihre Bewerbung!\n\nIhr Praxisteam der Zahnarztpraxis Muresan-Richter',
      is_active: true,
      order: 0
    });
    setNewRequirement('');
    setNewBenefit('');
    setMessage({ type: '', text: '' });
    if (editor) {
      editor.commands.setContent('');
    }
  };

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setFormData(prev => ({
        ...prev,
        requirements: [...prev.requirements, newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements.filter((_, i) => i !== index)
    }));
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit.trim()]
      }));
      setNewBenefit('');
    }
  };

  const removeBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  // Generate email link with subject
  const getEmailLink = (job) => {
    const subject = encodeURIComponent(`Bewerbung als ${job.title}`);
    const email = job.contact_email || 'info@zahnarzt-muresan.de';
    return `mailto:${email}?subject=${subject}`;
  };

  // Generate share links
  const getJobUrl = (job) => {
    return `${window.location.origin}/#jobs-${job.id}`;
  };

  const getWhatsAppLink = (job) => {
    const text = encodeURIComponent(`Schau dir diese Stellenanzeige an: ${job.title} - ${getJobUrl(job)}`);
    return `https://wa.me/?text=${text}`;
  };

  const getEmailShareLink = (job) => {
    const subject = encodeURIComponent(`Stellenanzeige: ${job.title}`);
    const body = encodeURIComponent(`Schau dir diese Stellenanzeige an:\n\n${job.title}\n${getJobUrl(job)}`);
    return `mailto:?subject=${subject}&body=${body}`;
  };

  const copyLink = async (job) => {
    try {
      await navigator.clipboard.writeText(getJobUrl(job));
      setCopiedId(job.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = getJobUrl(job);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(job.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5A7A6B]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Stellenanzeigen</h1>
          <p className="text-gray-600">Offene Stellen verwalten ({jobs.length} Anzeigen)</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-[#5A7A6B] hover:bg-[#4a6a5b] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Neue Stelle
        </Button>
      </div>

      {/* Job Listings List */}
      <div className="bg-white rounded-lg shadow">
        {jobs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Briefcase className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>Noch keine Stellenanzeigen vorhanden</p>
            <p className="text-sm mt-2">Klicken Sie auf "Neue Stelle" um eine Anzeige zu erstellen.</p>
          </div>
        ) : (
          <div className="divide-y">
            {jobs.map((job) => (
              <div key={job.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-800">{job.title}</h3>
                      {job.is_active ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Aktiv
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inaktiv
                        </span>
                      )}
                    </div>
                    {job.subtitle && (
                      <p className="text-sm text-gray-600 mt-1">{job.subtitle}</p>
                    )}
                    <div className="flex gap-4 mt-2 text-sm text-gray-500">
                      {job.location && <span>📍 {job.location}</span>}
                      {job.employment_type && <span>⏰ {job.employment_type}</span>}
                    </div>
                    
                    {/* Share & Apply Buttons */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      <a
                        href={getEmailLink(job)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-[#5A7A6B] text-white hover:bg-[#4a6a5b]"
                      >
                        <Mail className="w-3 h-3 mr-1" />
                        Bewerben
                      </a>
                      <a
                        href={getWhatsAppLink(job)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-green-500 text-white hover:bg-green-600"
                      >
                        <MessageCircle className="w-3 h-3 mr-1" />
                        WhatsApp
                      </a>
                      <a
                        href={getEmailShareLink(job)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-blue-500 text-white hover:bg-blue-600"
                      >
                        <Share2 className="w-3 h-3 mr-1" />
                        Teilen
                      </a>
                      <button
                        onClick={() => copyLink(job)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300"
                      >
                        {copiedId === job.id ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Kopiert!
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3 mr-1" />
                            Link kopieren
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(job)}
                      className="text-gray-600 hover:text-[#5A7A6B]"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(job.id)}
                      className="text-gray-600 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingJob ? 'Stellenanzeige bearbeiten' : 'Neue Stellenanzeige'}
              </h2>
              
              {/* Message */}
              {message.text && (
                <div className={`mb-4 p-3 rounded-lg ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                  {message.text}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stellentitel *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                      placeholder="z.B. Zahnmedizinische Fachangestellte (m/w/d)"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Untertitel
                    </label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                      placeholder="z.B. Werde Teil unseres Teams!"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Standort
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                      placeholder="z.B. Dachau"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Anstellungsart
                    </label>
                    <select
                      value={formData.employment_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, employment_type: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                    >
                      <option value="Vollzeit">Vollzeit</option>
                      <option value="Teilzeit">Teilzeit</option>
                      <option value="Vollzeit/Teilzeit">Vollzeit/Teilzeit</option>
                      <option value="Minijob">Minijob</option>
                      <option value="Ausbildung">Ausbildung</option>
                      <option value="Praktikum">Praktikum</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kontakt-E-Mail
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beschreibung *
                  </label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <MenuBar editor={editor} />
                    <EditorContent 
                      editor={editor} 
                      className="prose max-w-none p-3 min-h-[150px] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Requirements */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Anforderungen
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newRequirement}
                      onChange={(e) => setNewRequirement(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                      placeholder="z.B. Abgeschlossene Ausbildung als ZFA"
                    />
                    <Button type="button" onClick={addRequirement} className="bg-[#5A7A6B] hover:bg-[#4a6a5b]">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <ul className="space-y-1">
                    {formData.requirements.map((req, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded">
                        <span className="flex-1">{req}</span>
                        <button type="button" onClick={() => removeRequirement(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Benefits */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Wir bieten
                  </label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newBenefit}
                      onChange={(e) => setNewBenefit(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                      placeholder="z.B. Übertarifliche Bezahlung"
                    />
                    <Button type="button" onClick={addBenefit} className="bg-[#5A7A6B] hover:bg-[#4a6a5b]">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <ul className="space-y-1">
                    {formData.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm bg-gray-50 px-3 py-2 rounded">
                        <span className="flex-1">{benefit}</span>
                        <button type="button" onClick={() => removeBenefit(index)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Closing Text / Grußformel */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grußformel / Abschlusstext
                  </label>
                  <textarea
                    value={formData.closing_text}
                    onChange={(e) => setFormData(prev => ({ ...prev, closing_text: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5A7A6B] focus:border-transparent"
                    rows={3}
                    placeholder="z.B. Wir freuen uns auf Ihre Bewerbung!&#10;&#10;Ihr Praxisteam"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4 text-[#5A7A6B] border-gray-300 rounded focus:ring-[#5A7A6B]"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                    Stellenanzeige ist aktiv und öffentlich sichtbar
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={resetForm} disabled={saving}>
                    Abbrechen
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#5A7A6B] hover:bg-[#4a6a5b] text-white"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Speichern...
                      </>
                    ) : (
                      editingJob ? 'Speichern' : 'Erstellen'
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagement;
